import { PortalBrand } from "@/pages/PortalLogin";
import { useEffect, useState } from "react";
import { Link, Navigate, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, FileDown, Loader2, MapPin, Lock, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, downloadBlob, applyBrand } from "@/lib/api";
import { LenderLetterButton } from "@/components/LenderLetterButton";
import { EmailLenderLetter, PortalLetterSends } from "@/components/EmailLenderLetter";
import { SECTIONS } from "@/components/sectionConfig";
import { SectionCard } from "@/components/SectionCard";
import { FddVerified } from "@/components/FddVerified";
import { ScoreRing, VerdictBadge } from "@/components/ScoreRing";
import { PropertyPhotos } from "@/components/PropertyPhotos";
import { LiveSiteData } from "@/components/LiveSiteData";
import { SiteMap } from "@/components/SiteMap";
import { CoverPage } from "@/components/CoverPage";
import { ImprovementReport } from "@/components/PrintExtras";
import { PortalImproveRequest } from "@/components/PortalImproveRequest";
import { PortalRevisions } from "@/components/PortalRevisions";
import { PortalFeedback } from "@/components/Feedback";

export default function PortalStudy() {
  const { id } = useParams();
  const [me, setMe] = useState(null);
  const [study, setStudy] = useState(null);
  const [error, setError] = useState("");
  const [sends, setSends] = useState(0);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/portal/me").then((r) => { setMe(r.data); applyBrand(r.data.brand); }).catch(() => setMe(false));
    api.get(`/portal/studies/${id}/view`).then((r) => setStudy(r.data))
      .catch((e) => (e.response?.status === 402 ? navigate(`/portal/studies/${id}/preview`, { replace: true }) : setError("Study not found.")));
  }, [id, navigate]);

  if (me === false) return <Navigate to="/portal/login" replace />;
  if (error) return (
    <div className="min-h-screen grid place-items-center text-center px-4" data-testid="portal-study-error">
      <div><Lock className="h-8 w-8 text-amber-400 mx-auto mb-3" /><p className="text-slate-300 mb-4">{error}</p><Link to="/portal" className="text-emerald-400 underline">Back to portal</Link></div>
    </div>
  );
  if (!me || !study) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-400" /></div>;

  const inp = study.input;
  const currency = inp.currency || "USD";
  const sections = SECTIONS.filter((s) => !s.optional || study.sections?.[s.key]);
  const word = async () => {
    setBusy(true);
    try { await downloadBlob(`/portal/studies/${id}/docx`, `${(inp.project_name || "study").normalize("NFKD").replace(/[^\w]+/g, "-")}.docx`); }
    catch { toast.error("Download failed"); } finally { setBusy(false); }
  };
  const print = () => { setTimeout(() => window.print(), 200); };
  const pdf = async () => {
    setBusy("pdf");
    try { await downloadBlob(`/portal/studies/${id}/pdf`, `${(inp.project_name || "study").normalize("NFKD").replace(/[^\w]+/g, "-")}.pdf`); }
    catch { toast.error("PDF download failed"); } finally { setBusy(false); }
  };

  return (
    <div className="print-light min-h-screen pb-24 md:pb-0" data-testid="portal-study-page">
      <CoverPage basePath="/portal/studies" study={study} client={{ name: me.name, company: me.company }} settings={{ company_name: me.company_name, tagline: me.tagline, logo_path: me.has_logo ? "portal" : null, cover_template: me.cover_template }} />
      <ImprovementReport imp={study.last_improvement} />
      <header className="no-print border-b border-slate-800 bg-slate-950/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/portal" data-testid="portal-study-back" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white"><ArrowLeft className="h-4 w-4" />My portal</Link>
          <PortalBrand branding={me} compact />
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 flex flex-col md:flex-row md:items-center justify-between gap-6 border-b border-slate-800">
        <div className="flex items-center gap-6">
          <ScoreRing score={study.score} size={84} />
          <div>
            <div className="flex items-center gap-3 mb-2"><span className="text-xs uppercase tracking-wider text-slate-500">{inp.industry || "Feasibility study"}</span><VerdictBadge verdict={study.verdict} /></div>
            <h1 data-testid="portal-study-title" className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{inp.project_name}</h1>
            {inp.address && <p className="flex items-center gap-1 text-sm text-cyan-300 mt-2"><MapPin className="h-4 w-4" />{inp.address}</p>}
          </div>
        </div>
        <div className="no-print flex flex-wrap gap-3">
          <Button data-testid="portal-study-word" variant="outline" disabled={busy === true} onClick={word} className="rounded-full border-slate-700 bg-transparent">{busy === true ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}Word</Button>
          <LenderLetterButton path={`/portal/studies/${id}/lender-letter`} name={inp.project_name} testId="portal-lender-letter" />
          <EmailLenderLetter studyId={id} onSent={() => setSends((n) => n + 1)} />
          <Button data-testid="portal-study-print" variant="ghost" onClick={print} className="rounded-full"><Printer className="h-4 w-4 mr-2" />Print</Button>
          <Button data-testid="portal-study-pdf" disabled={busy === "pdf"} onClick={pdf} className="rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400">{busy === "pdf" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}Download PDF</Button>
        </div>
      </div>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10 space-y-8">
        <PortalLetterSends studyId={id} refresh={sends} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <PortalFeedback studyId={id} />
          <PortalRevisions studyId={id} sections={sections} study={study} />
        </div>
        <PortalImproveRequest studyId={id} score={study.score} />
        {sections.map((cfg, i) => (
          <SectionCard key={cfg.key} cfg={cfg} index={i} data={study.sections?.[cfg.key]} currency={currency} readOnly>
            {cfg.key === "franchise_economics" && <FddVerified fdd={inp.fdd} />}
            {cfg.key === "site" && (
              <>
                <PropertyPhotos studyId={id} basePath="/portal/studies" photos={study.site_data?.photos} version={study.site_data?.fetched_at || ""} />
                <div className="mb-6"><SiteMap address={inp.address} /></div>
                <LiveSiteData data={study.site_data} />
              </>
            )}
          </SectionCard>
        ))}
        <p className="text-xs text-slate-500 text-center pb-6">Prepared by {study.prepared_by || me.company_name} · {me.company_name}</p>
      </main>
      <div className="no-print md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-slate-800 bg-slate-950/95 backdrop-blur-xl px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] grid grid-cols-2 gap-3" data-testid="portal-sticky-actions">
        <Button data-testid="portal-sticky-pdf" disabled={busy === "pdf"} onClick={pdf} className="h-11 rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400">{busy === "pdf" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}Download PDF</Button>
        <LenderLetterButton path={`/portal/studies/${id}/lender-letter`} name={inp.project_name} testId="portal-sticky-lender-letter" className="h-11" />
      </div>
    </div>
  );
}
